import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Badge } from "react-bootstrap";
import Modal from 'react-bootstrap/Modal';
import { ButtonComponent } from "../elements";
import * as GastosAPI from "../../api/gastos.api.ts";

export default function GastosTableComponent({ thead, tbody, onGastoEliminado }) {
    const { t } = useContext(TranslatorContext);
    const [alertModal, setAlertModal] = useState(false);
    const [data, setData] = useState([]);
    const [gastoAEliminar, setGastoAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    useEffect(()=> { setData(tbody) }, [tbody]);

    // Memoizar formatCurrency para evitar recreaciones
    const formatCurrency = useMemo(() => {
        return (amount) => {
            // Si es un número, usarlo directamente
            if (typeof amount === 'number') {
                return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                }).format(amount);
            }
            
            // Si es string, convertir cuidadosamente
            let numAmount = 0;
            if (typeof amount === 'string') {
                // Remover caracteres no numéricos excepto punto y coma
                const cleanAmount = amount.replace(/[^\d.,]/g, '');
                // Usar parseFloat directamente
                numAmount = parseFloat(cleanAmount) || 0;
            }
            
            return new Intl.NumberFormat('es-CL', {
                style: 'currency',
                currency: 'CLP',
                minimumFractionDigits: 0
            }).format(numAmount);
        };
    }, []);

    // Memoizar getStatusBadge
    const getStatusBadge = useCallback((status, estado_class) => {
        const variants = {
            success: 'success',
            warning: 'warning', 
            danger: 'danger',
            info: 'info'
        };
        return <Badge bg={variants[estado_class] || 'secondary'}>{status}</Badge>;
    }, []);

    // Memoizar getCategoryColor
    const getCategoryColor = useCallback((category, categoryColor) => {
        // Si viene el color de la categoría desde la API, usarlo
        if (categoryColor) {
            return categoryColor;
        }
        
        // Colores por defecto por nombre de categoría
        const colors = {
            'Gastos Oficina': '#dc3545',
            'Edmundo Neira': '#0dcaf0',
            'Operativa/Terreno': '#198754',
            'Egresos Operativos': '#ffc107',
            'Sin categoría': '#6c757d'
        };
        return colors[category] || '#6c757d';
    }, []);

    // Función para imprimir comprobante rápido (optimizada con useCallback)
    const imprimirComprobanteRapido = useCallback(async (gastoId) => {
        try {
            const response = await GastosAPI.getGasto(gastoId);
            if (!response.success || !response.data) {
                alert('No se pudo obtener los datos del gasto para imprimir');
                return;
            }
            
            const gasto = response.data;
            
            // Función para formatear moneda
            const formatCurrency = (amount) => {
                const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
                return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                }).format(numAmount);
            };
            
            // Función para formatear fecha
            const formatDate = (dateString) => {
                if (!dateString) return 'No especificada';
                const date = new Date(dateString);
                return date.toLocaleDateString('es-CL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            };
            
            // Crear ventana de impresión
            const ventanaImpresion = window.open('', '_blank', 'width=800,height=600');
            
            const contenidoComprobante = `
                <!DOCTYPE html>
                <html lang="es">
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>Comprobante de Gasto - ${gasto.gas_codigo}</title>
                    <style>
                        body {
                            font-family: 'Arial', sans-serif;
                            margin: 20px;
                            color: #333;
                            line-height: 1.6;
                        }
                        .header {
                            text-align: center;
                            border-bottom: 3px solid #007bff;
                            padding-bottom: 20px;
                            margin-bottom: 30px;
                        }
                        .header h1 {
                            color: #007bff;
                            margin: 0;
                            font-size: 28px;
                        }
                        .header h2 {
                            color: #666;
                            margin: 5px 0 0 0;
                            font-size: 18px;
                            font-weight: normal;
                        }
                        .comprobante-info {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 30px;
                            background-color: #f8f9fa;
                            padding: 15px;
                            border-radius: 8px;
                        }
                        .info-section {
                            display: grid;
                            grid-template-columns: repeat(2, 1fr);
                            gap: 20px;
                            margin-bottom: 30px;
                        }
                        .info-group {
                            border: 1px solid #e9ecef;
                            padding: 15px;
                            border-radius: 6px;
                            background-color: #fff;
                        }
                        .info-label {
                            font-weight: bold;
                            color: #495057;
                            font-size: 12px;
                            text-transform: uppercase;
                            margin-bottom: 5px;
                            display: block;
                        }
                        .info-value {
                            font-size: 14px;
                            color: #212529;
                        }
                        .monto-section {
                            text-align: center;
                            background: linear-gradient(135deg, #28a745, #20c997);
                            color: white;
                            padding: 20px;
                            border-radius: 10px;
                            margin: 30px 0;
                        }
                        .monto-value {
                            font-size: 36px;
                            font-weight: bold;
                            margin: 0;
                        }
                        .monto-label {
                            font-size: 16px;
                            opacity: 0.9;
                            margin: 5px 0 0 0;
                        }
                        .status-badge {
                            display: inline-block;
                            padding: 6px 12px;
                            border-radius: 20px;
                            font-size: 12px;
                            font-weight: bold;
                            text-transform: uppercase;
                        }
                        .status-aprobado { background-color: #d4edda; color: #155724; }
                        .status-pendiente { background-color: #fff3cd; color: #856404; }
                        .status-rechazado { background-color: #f8d7da; color: #721c24; }
                        .status-borrador { background-color: #e2e3e5; color: #383d41; }
                        .footer {
                            margin-top: 40px;
                            text-align: center;
                            font-size: 12px;
                            color: #6c757d;
                            border-top: 1px solid #e9ecef;
                            padding-top: 20px;
                        }
                        .categoria-indicator {
                            width: 20px;
                            height: 20px;
                            border-radius: 4px;
                            display: inline-block;
                            margin-right: 8px;
                            vertical-align: middle;
                        }
                        @media print {
                            body { margin: 0; }
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>COMPROBANTE DE GASTO</h1>
                        <h2>Sistema de Gestión Cristour</h2>
                    </div>

                    <div class="comprobante-info">
                        <div>
                            <strong>Código:</strong> ${gasto.gas_codigo}<br>
                            <strong>Estado:</strong> 
                            <span class="status-badge status-${gasto.gas_estado?.toLowerCase() || 'borrador'}">
                                ${gasto.gas_estado || 'BORRADOR'}
                            </span>
                        </div>
                        <div style="text-align: right;">
                            <strong>Fecha de emisión:</strong><br>
                            ${new Date().toLocaleDateString('es-CL', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </div>
                    </div>

                    <div class="monto-section">
                        <p class="monto-value">${formatCurrency(gasto.gas_monto)}</p>
                        <p class="monto-label">Monto del Gasto</p>
                    </div>

                    <div class="info-section">
                        <div class="info-group">
                            <span class="info-label">Descripción</span>
                            <div class="info-value">${gasto.gas_descripcion}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Responsable</span>
                            <div class="info-value">${gasto.gas_responsable}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Categoría</span>
                            <div class="info-value">
                                <span class="categoria-indicator" style="background-color: ${gasto.categoria_color || '#6c757d'};"></span>
                                ${gasto.categoria_nombre || 'No especificada'}
                            </div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Subcategoría</span>
                            <div class="info-value">${gasto.subcategoria_nombre || 'No especificada'}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Tipo de Gasto</span>
                            <div class="info-value">${gasto.tipo_nombre || 'No especificado'}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Fondo</span>
                            <div class="info-value">${gasto.fondo_nombre || 'Sin fondo asignado'}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Fecha del Gasto</span>
                            <div class="info-value">${formatDate(gasto.gas_fecha_gasto)}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Año Presupuesto</span>
                            <div class="info-value">${gasto.gas_anio_presupuesto}</div>
                        </div>
                    </div>

                    ${gasto.gas_observaciones ? `
                        <div class="info-group" style="grid-column: 1 / -1;">
                            <span class="info-label">Observaciones</span>
                            <div class="info-value">${gasto.gas_observaciones}</div>
                        </div>
                    ` : ''}

                    <div class="footer">
                        <p>Este comprobante es un documento informativo generado automáticamente por el sistema.</p>
                        <p>Fecha de creación del gasto: ${formatDate(gasto.gas_fecha_creacion)}</p>
                    </div>

                    <div class="no-print" style="text-align: center; margin-top: 30px;">
                        <button onclick="window.print()" style="
                            background-color: #007bff;
                            color: white;
                            border: none;
                            padding: 12px 24px;
                            border-radius: 6px;
                            font-size: 16px;
                            cursor: pointer;
                            margin-right: 10px;
                        ">Imprimir</button>
                        <button onclick="window.close()" style="
                            background-color: #6c757d;
                            color: white;
                            border: none;
                            padding: 12px 24px;
                            border-radius: 6px;
                            font-size: 16px;
                            cursor: pointer;
                        ">Cerrar</button>
                    </div>
                </body>
                </html>
            `;
            
            ventanaImpresion.document.write(contenidoComprobante);
            ventanaImpresion.document.close();
            ventanaImpresion.focus();
            
        } catch (error) {
            console.error('Error al imprimir comprobante:', error);
            alert('Error al generar el comprobante de impresión');
        }
    }, []);

    // Funciones para manejar la eliminación de gastos (memoizadas)
    const confirmarEliminacion = useCallback((gasto) => {
        setGastoAEliminar(gasto);
        setAlertModal(true);
    }, []);

    const cancelarEliminacion = useCallback(() => {
        setGastoAEliminar(null);
        setAlertModal(false);
    }, []);

    const eliminarGasto = useCallback(async () => {
        if (!gastoAEliminar) return;

        try {
            setEliminando(true);
            
            const resultado = await GastosAPI.eliminarGasto(gastoAEliminar.gas_id || gastoAEliminar.id);
            
            if (resultado.success) {
                // Mostrar mensaje de éxito
                alert('Gasto eliminado exitosamente');
                
                // Actualizar la lista de gastos localmente
                setData(prevData => prevData.filter(item => 
                    (item.gas_id || item.id) !== (gastoAEliminar.gas_id || gastoAEliminar.id)
                ));
                
                // Notificar al componente padre si existe el callback
                if (onGastoEliminado) {
                    onGastoEliminado(gastoAEliminar.gas_id || gastoAEliminar.id);
                }
                
                // Cerrar modal
                cancelarEliminacion();
                
            } else {
                // Mostrar error específico del servidor
                alert(resultado.message || 'No se pudo eliminar el gasto');
            }
            
        } catch (error) {
            console.error('Error al eliminar gasto:', error);
            alert('Error al conectar con el servidor');
        } finally {
            setEliminando(false);
        }
    }, [gastoAEliminar, onGastoEliminado, cancelarEliminacion]);

    // Verificar si un gasto se puede eliminar (memoizado)
    const puedeEliminar = useCallback((gasto) => {
        const estado = gasto.gas_estado || gasto.status || '';
        return estado.toLowerCase() === 'pendiente' || estado.toLowerCase() === 'borrador';
    }, []);

    return (
        <div className="mc-table-responsive">
            <table className="mc-table product">
                <thead className="mc-table-head primary">
                    <tr>
                        {thead?.map((item, index) => (
                            <th key={ index }>{ t(item.head) }</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="mc-table-body even">
                    {data?.map((item, index) => (
                        <tr key={ index }> 
                            <td>
                                <span className="badge bg-light text-dark">{item.gas_codigo || item.id}</span>
                            </td>
                            <td>
                                <div className="mc-table-product md">
                                    <div className="mc-table-group">
                                        <h6 className="mb-1">{item.gas_descripcion || item.heading}</h6>
                                        <p className="mb-0 text-muted small">
                                            {item.gas_observaciones || item.describe || 'Sin observaciones'}
                                        </p>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="me-2" 
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: getCategoryColor(
                                                item.categoria_nombre || item.category,
                                                item.categoria_color
                                            ),
                                            borderRadius: '2px'
                                        }}
                                    ></div>
                                    <span className="small">{item.categoria_nombre || item.category}</span>
                                </div>
                            </td>
                            <td>
                                <strong style={{ 
                                    color: getCategoryColor(
                                        item.categoria_nombre || item.category, 
                                        item.categoria_color
                                    ) 
                                }}>
                                    {formatCurrency(item.gas_monto || item.comuna)}
                                </strong>
                            </td>
                            <td>
                                <span className="small">
                                    {item.gas_fecha_gasto ? 
                                        new Date(item.gas_fecha_gasto).toLocaleDateString('es-CL') : 
                                        (item.brand ? new Date(item.brand).toLocaleDateString('es-CL') : 'Sin fecha')
                                    }
                                </span>
                            </td>
                            <td>
                                {getStatusBadge(item.gas_estado || item.status, item.estado_class || 'info')}
                            </td>
                            <td>
                                <div className="mc-table-action">
                                    <Link to={`/gastos/ver/${item.gas_id || item.id}`}>
                                        <ButtonComponent 
                                            className="material-icons" 
                                            type="button" 
                                            variant="outline-dark"
                                            title="Ver detalle"
                                        >
                                            {item.action?.view || 'visibility'}
                                        </ButtonComponent>
                                    </Link>
                                    
                                    <Link to={`/gastos/editar/${item.gas_id || item.id}`}>
                                        <ButtonComponent 
                                            className="material-icons ms-1" 
                                            type="button" 
                                            variant="outline-warning"
                                            title="Editar"
                                        >
                                            {item.action?.edit || 'edit'}
                                        </ButtonComponent>
                                    </Link>
                                    
                                    <ButtonComponent 
                                        className="material-icons ms-1" 
                                        type="button" 
                                        variant="outline-info"
                                        title="Imprimir Comprobante"
                                        onClick={() => imprimirComprobanteRapido(item.gas_id || item.id)}
                                    >
                                        print
                                    </ButtonComponent>
                                    
                                    <ButtonComponent 
                                        className="material-icons ms-1" 
                                        type="button" 
                                        variant={puedeEliminar(item) ? "outline-danger" : "outline-secondary"}
                                        title={puedeEliminar(item) ? "Eliminar" : "No se puede eliminar (estado no válido)"}
                                        onClick={() => puedeEliminar(item) ? confirmarEliminacion(item) : null}
                                        disabled={!puedeEliminar(item)}
                                    >
                                        {item.action?.delete || 'delete'}
                                    </ButtonComponent>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal show={alertModal} onHide={cancelarEliminacion}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {gastoAEliminar && (
                        <div>
                            <p>¿Está seguro que desea eliminar el siguiente gasto?</p>
                            <div className="alert alert-info">
                                <strong>Código:</strong> {gastoAEliminar.gas_codigo || gastoAEliminar.id}<br/>
                                <strong>Descripción:</strong> {gastoAEliminar.gas_descripcion || gastoAEliminar.heading}<br/>
                                <strong>Monto:</strong> {formatCurrency(gastoAEliminar.gas_monto || gastoAEliminar.price)}
                            </div>
                            <p className="text-danger">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Esta acción no se puede deshacer.
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <ButtonComponent 
                        variant="secondary" 
                        onClick={cancelarEliminacion}
                        disabled={eliminando}
                    >
                        Cancelar
                    </ButtonComponent>
                    <ButtonComponent 
                        variant="danger" 
                        onClick={eliminarGasto}
                        disabled={eliminando}
                    >
                        {eliminando ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Eliminando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-trash me-2"></i>
                                Eliminar
                            </>
                        )}
                    </ButtonComponent>
                </Modal.Footer>
            </Modal>
        </div>
    );
}